import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccountInfo } from '../api/queryHooks/account';
import { consentRegistry } from './registry';
import { activeServices, revisionFor } from './activeServices';
import { buildConsentConfig } from './consentConfig';
import { getConsentApi, setConsentApi } from './consentState';

/**
 * Mounts vanilla-cookieconsent for shops that have at least one active
 * consentable service (src/consent/services.ts). For every other shop —
 * all of them today — this renders nothing and never even fetches the
 * library chunk, so there is no banner, no cookie and no DOM node.
 *
 * Re-runs when the active set, the revision or the UI language changes;
 * the library is reset (keeping the visitor's cookie) and started again
 * with the rebuilt config.
 */
const ConsentManager = () => {
  const { data: accountInfo } = useAccountInfo();
  const { t, i18n } = useTranslation();
  const active = useMemo(() => activeServices(consentRegistry(), accountInfo), [accountInfo]);
  const activeIds = active.map((s) => s.id).join(',');
  const revision = revisionFor(active);
  const language = i18n.language;

  useEffect(() => {
    if (active.length === 0) return;
    let cancelled = false;
    void Promise.all([import('vanilla-cookieconsent'), import('vanilla-cookieconsent/dist/cookieconsent.css')])
      .then(([cc]) => {
        if (cancelled) return;
        // A previous run left modals in the DOM; reset() (without erasing the
        // cookie) tears them down so the rebuilt config replaces them instead
        // of stacking a second banner on top.
        if (getConsentApi()) cc.reset();
        void cc.run(buildConsentConfig({ active, revision, language, t }));
        setConsentApi(cc);
      });
    return () => { cancelled = true; };
    // `active` is derived from activeIds; listing the string keeps the deps stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIds, revision, language]);

  return null;
};

export default ConsentManager;
